import { shallowEqual } from "react-redux";

import type { WirePart } from "../protocol/protocol";

type Language = "js" | "python" | "bash";
type CellPart = Extract<WirePart, { kind: "sandbox_cell" }>;

/** What a finished command or cell printed, its fences gone (`note` is the model's, never shown). */
export interface ExecOutput {
  stdout: string;
  stderr: string;
  /** A repl's last expression, printed after its output. */
  value: string | null;
  exitCode: number;
  timedOut: boolean;
  truncated: boolean;
  durationMs: number;
}

/** A code-mode cell: what it did, never its code (which only the debug view shows). */
export interface CellSummary {
  language: Language;
  summary: string;
  calls: { tool: string; outcome: string }[];
  approvals: string[];
  durationMs: number;
}

/**
 * A sandbox call's card under its line: a command or repl's output tail (`exec`), or a code
 * cell's summary (`cell`). `code` is what ran, for the debug view; a stored cell part has none.
 */
export type Step =
  | { kind: "exec"; code: string; output: ExecOutput | null }
  | { kind: "cell"; code: string | null; cell: CellSummary };

const LANGUAGES: readonly string[] = ["js", "python", "bash"];

const record = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
const string = (value: unknown): string => (typeof value === "string" ? value : "");

/** The sandbox's text without the fence the server put round it (a line of 3+ backticks each side). */
export function unfence(text: string): string {
  const fenced = /^(`{3,})\n([\s\S]*)\n\1$/.exec(text);
  return fenced ? fenced[2] : text;
}

function execOutput(content: unknown): ExecOutput | null {
  const result = record(content);
  if (!result || typeof result.exit_code !== "number") return null;
  return {
    stdout: unfence(string(result.stdout)),
    stderr: unfence(string(result.stderr)),
    value: typeof result.value === "string" ? unfence(result.value) : null,
    exitCode: result.exit_code,
    timedOut: result.timed_out === true,
    truncated: result.truncated === true,
    durationMs: typeof result.duration_ms === "number" ? result.duration_ms : 0,
  };
}

// The server's cell summary (#927 backend/apps/agentic/codemode/cell.py cell_summary): segments
// joined by SEPARATOR, "Ran code" (EMPTY_LINE) when there are none, and its approvals as
// f"Asked you to approve {plural(waiting, 'change')}".
const SEPARATOR = " · ";
const EMPTY_LINE = "Ran code";
const ASKED = /^Asked you to approve \d+ changes?$/;

/**
 * The server's summary counting the approvals that show (`shown`, the cards after the cell): one
 * expired, or not held by the thread, is neither shown nor counted. A summary in another wording
 * is left as it is.
 */
export function shownApprovals(summary: string, shown: number): string {
  const segments = summary.split(SEPARATOR);
  const index = segments.findIndex((segment) => ASKED.test(segment));
  if (index < 0) return summary;
  if (shown) segments[index] = `Asked you to approve ${shown} change${shown === 1 ? "" : "s"}`;
  else segments.splice(index, 1);
  return segments.join(SEPARATOR) || EMPTY_LINE;
}

/** A sandbox_cell part's fields, or null when they are not one; `approvals` show (`shownApprovals`). */
export function cellSummary(source: unknown, approvals: number): CellSummary | null {
  const cell = record(source);
  if (!cell || !LANGUAGES.includes(cell.language as string)) return null;
  const calls = Array.isArray(cell.calls) ? cell.calls.map(record) : [];
  return {
    language: cell.language as Language,
    summary: shownApprovals(string(cell.summary), approvals),
    calls: calls.flatMap((call) =>
      call ? [{ tool: string(call.tool), outcome: string(call.outcome) }] : []
    ),
    approvals: Array.isArray(cell.approvals) ? cell.approvals.map(string) : [],
    durationMs: typeof cell.duration_ms === "number" ? cell.duration_ms : 0,
  };
}

/**
 * The card a stored call shows under its line: bash and repl only (any other tool, and a live
 * call, which has no name yet, stay a plain line). A repl in code mode carries its cell (`card`),
 * with the number of its approvals that show.
 */
export function toolStep(
  name: string,
  args: Record<string, unknown>,
  result: Extract<WirePart, { kind: "tool_result" }> | null,
  approvals = 0
): Step | null {
  if (name !== "bash" && name !== "repl") return null;
  const code = string(name === "bash" ? args.command : args.code);
  const card = record(result?.card);
  const cell = card?.kind === "sandbox_cell" ? cellSummary(card, approvals) : null;
  if (cell) return { kind: "cell", code, cell };
  return { kind: "exec", code, output: result ? execOutput(result.content) : null };
}

/** A sandbox_cell message part's card: the same as a code-mode repl's, without the code. */
export function cellStep(part: CellPart, approvals = 0): Step | null {
  const cell = cellSummary(part, approvals);
  return cell && { kind: "cell", code: null, cell };
}

/** Steps are rebuilt with every update, so they compare by content. */
export function sameStep(a: Step | null, b: Step | null): boolean {
  if (!a || !b) return a === b;
  if (a.kind === "exec" && b.kind === "exec") {
    return (
      a.code === b.code &&
      (a.output && b.output ? shallowEqual(a.output, b.output) : a.output === b.output)
    );
  }
  if (a.kind === "cell" && b.kind === "cell") {
    const [x, y] = [a.cell, b.cell];
    return (
      a.code === b.code &&
      x.language === y.language &&
      x.summary === y.summary &&
      x.durationMs === y.durationMs &&
      x.calls.length === y.calls.length &&
      x.calls.every((call, i) => shallowEqual(call, y.calls[i])) &&
      x.approvals.length === y.approvals.length &&
      x.approvals.every((id, i) => id === y.approvals[i])
    );
  }
  return false;
}

/** "0.4 s", "2 s", "1 min 5 s". */
export function formatDuration(ms: number): string {
  if (ms < 950) return `${Math.round(ms / 100) / 10} s`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} s`;
  const [minutes, rest] = [Math.floor(seconds / 60), seconds % 60];
  return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
}

// A terminal's colour and cursor codes, and its commands that carry text (a window title).
// eslint-disable-next-line no-control-regex
const ANSI = /\x1b\[[0-9;?]*[A-Za-z]/g;
// eslint-disable-next-line no-control-regex
const OSC = /\x1b\][^\x07\x1b]*(?:\x07|\x1b\\)/g;

/** A line of sandbox output as plain text: no terminal codes, and no control or format characters
 * (a right-to-left override would make it read as something else), as the server's lines do. */
function plainLine(line: string): string {
  return line.replace(OSC, "").replace(ANSI, "").replace(/\t/g, " ").replace(/\p{C}/gu, "");
}

/** The output's lines worth showing: plain text, blank ones dropped. */
function shownLines(text: string): string[] {
  return text
    .split(/\r\n|\r|\n/)
    .map(plainLine)
    .filter((line) => line.trim());
}

// How much of a command's output its card shows: the last 5 lines, each at most 200 characters.
const TAIL_LINES = 5;
const LINE_WIDTH = 200;

/** A very long line cut to its end. */
const fitted = (line: string, width = LINE_WIDTH): string =>
  line.length > width ? `…${line.slice(-width)}` : line;

/** The output's last lines worth showing, a very long one cut to its end. */
export function outputTail(text: string, lines = TAIL_LINES, width = LINE_WIDTH): string[] {
  return shownLines(text)
    .slice(-lines)
    .map((line) => fitted(line, width));
}

// An error's first line: "TypeError: x is null", a bare "ValidationError", "DOMException: ...".
const ERROR_HEADER = /^(?:[A-Za-z_$][\w$]*)?(?:Error|Exception)(?::.*)?$/;
const TRACEBACK = /^Traceback \(most recent call last\):$/;
// The realm's notes after the thrown error (background.ts settle): "[the cell did not await ...]".
const CELL_NOTE = /^\[the cell .*\]$/;

/** A failure's last lines, cut from the middle when the tail would drop the error's header (a
 * long message): the header, "…", then the last 3. The realm writes the thrown error as its
 * header and at most 5 more lines (jsRealm errorText's 6-line cut), then its notes; so the first
 * header in the 6 lines before the notes is that error's, not a header-shaped line of its message
 * ("Error: Import failed" over "TypeError: a"...). With none there, the last one, so
 * console.error output before the throw never stands in for it. A very long header keeps its
 * start, so its name and first words show. A Python traceback ends at its header already: the
 * plain tail, as it was, unless a JS error's message carries it (its header comes first). */
function errorTail(text: string): string[] {
  const shown = shownLines(text);
  const notes = shown.length - 1 - shown.findLastIndex((line) => !CELL_NOTE.test(line));
  const realm = shown.length - notes - TAIL_LINES - 1;
  const first = shown.findIndex((line, at) => at >= realm && ERROR_HEADER.test(line));
  const header = first >= 0 ? first : shown.findLastIndex((line) => ERROR_HEADER.test(line));
  const traceback = shown.findIndex((line) => TRACEBACK.test(line));
  if (traceback >= 0 && (header < 0 || traceback < header)) return outputTail(text);
  const tail = shown.map((line, at) =>
    at === header && line.length > LINE_WIDTH ? `${line.slice(0, LINE_WIDTH)}…` : fitted(line)
  );
  return header >= 0 && header < shown.length - TAIL_LINES
    ? [tail[header], "…", ...tail.slice(-(TAIL_LINES - 2))]
    : tail.slice(-TAIL_LINES);
}

// A JavaScript stack's frames: V8's "    at f (file:1:2)", Firefox's and Safari's "f@file:1:2"
// ("global code@file:1:2"), Firefox's code run by eval or new Function ("@file line 482 >
// Function:6:7", nested ones "... line 3 > eval line 1 > Function:2:5"), Safari's built-ins,
// "forEach@[native code]", and its frames with no location ("eval code@", "@"). A frame's name
// has no colon, so a message ("Error: failed in forEach@[native code]") is never taken for one;
// a location-less one is a bare name or JSC's own, so a sentence ending in "@" is not either.
const STACK_FRAME =
  /^\s+at\s|^[^@:]*@\S+(?: line \d+ > (?:eval|Function))*:\d+:\d+$|^[^@:]*@\[native code\]$|^(?:[\w$.<>*]*|eval code|global code|module code)@$/;

/** Errors without their stack frames, so the tail keeps the message line ("Error: boom") the
 * frames would push out; the frames stay in the inspector. All frames: the text as it is. */
function withoutFrames(text: string): string {
  const kept = text.split(/\r\n|\r|\n/).filter((line) => !STACK_FRAME.test(plainLine(line)));
  return kept.some((line) => line.trim()) ? kept.join("\n") : text;
}

/** What a finished command shows of its output: its errors when it failed or printed nothing
 * else (design ruling d4550fd0: stderr only on failure, stdout when stderr is empty). A failure
 * keeps its error's header when a long message would push it out (errorTail). A timeout also
 * keeps what it printed, before its errors: the stop note ends the text, so the tail keeps it,
 * and long errors (a traceback) push the output out first. */
export function execLines(output: ExecOutput): string[] {
  const failed = output.exitCode !== 0 || output.timedOut;
  const printed = [output.stdout, output.value ?? ""].filter(Boolean).join("\n");
  if (!((failed || !printed.trim()) && output.stderr.trim())) return outputTail(printed);
  if (!failed) return outputTail(output.stderr);
  const errors = withoutFrames(output.stderr);
  if (output.timedOut) return outputTail([printed, errors].filter(Boolean).join("\n"));
  return errorTail(errors);
}

// bash's line (backend/apps/sandbox/tools/shell.py _ran): 'Ran "<command>"' or "Ran a command",
// then ", exit N" and ": <last line>", or ": it timed out". The command is inert_text, which drops
// every quote, so the first closing quote ends it.
const BASH_LINE = /^(Ran (?:"[^"]*"|a command))(?:, exit -?\d+)?(?:: .*)?$/s;

/**
 * The line over a step: a command whose output shows below says only how it ended, not its last
 * line again ('Ran "python3 totals.py", exit 2'). Any other line is the server's, as it is.
 */
export function stepLine(line: string, step: Step | null): string {
  const output = step?.kind === "exec" ? step.output : null;
  const ran = output && execLines(output).length ? BASH_LINE.exec(line) : null;
  if (!output || !ran) return line;
  if (output.timedOut) return `${ran[1]}, it timed out`;
  return output.exitCode ? `${ran[1]}, exit ${output.exitCode}` : ran[1];
}
